// laneOffset — symmetric per-route lane displacement so multiple
// patrol routes' polylines don't overlap when they share a corridor.
//
// THE PROBLEM:
//
//   gridAstar runs A* once per (start, end) pair. If route A and route
//   B both pass through the same corridor between their respective
//   locations, A* picks the same cells for both — the two routes'
//   polylines land on exactly the same pixel line, so visually they
//   merge into one and the operator can't tell which route is which.
//
//   Perpendicular crossings (a horizontal segment of route A crossing
//   a vertical segment of route B) are fine — they intersect at a
//   single point, not running along each other.
//
// WHAT THIS DOES:
//
//   After computing each route's polyline, run them through
//   applyLaneOffsets. The pass scans for "parallel-overlap groups":
//   sets of axis-aligned segments that share a fixed coordinate (with
//   a small tolerance) AND have overlapping ranges along the varying
//   coordinate, AND come from different routes.
//
//   For each such group, sort the unique routes by index and assign
//   each a SYMMETRIC lane index — e.g. for 2 routes the lanes are
//   {-0.5, +0.5}; for 3 routes {-1, 0, +1}; for 4 routes {-1.5, -0.5,
//   +0.5, +1.5}. This keeps the visual "centre of mass" of the
//   overlapping routes anchored on the original corridor centreline,
//   rather than shoving the whole bundle off to one side.
//
//   Each segment's lane × gapPx is its perpendicular displacement.
//   Horizontal segments shift in y; vertical segments shift in x.
//
// CORNER PRESERVATION:
//
//   Polylines from gridAstar are sequences of L-shaped corners. If we
//   naively offset each segment, the corners between two offset
//   segments no longer connect. We compensate: each interior corner
//   gets the y from its incoming H segment's offset AND the x from
//   its outgoing V segment's offset (or vice versa for V→H corners).
//
//   First and last polyline points are kept at the original marker
//   pin coordinates — the path still attaches to the location
//   markers (otherwise the rendered route wouldn't visually begin /
//   end at the right place). The transition segment from marker to
//   first-offset-corner is allowed to be a short diagonal — not
//   strictly orthogonal but visually acceptable as an "approach line".
//
// NOT DONE (deliberate, see PR commentary):
//
//   - Diagonal source segments are left unchanged (no offset). These
//     are rare (gridAstar emits axis-aligned segments by construction)
//     but possible at the first/last "approach" segments.
//   - Partial overlap: if route A and route B overlap on only a sub-
//     range of an otherwise long segment, the whole segment of B gets
//     the offset, including the non-overlap portion. Subdividing the
//     segment to offset only the overlap region is plan-C territory
//     and not worth the corner-stitching complexity right now.

import type { Point } from "./gridAstar.js";

export interface RoutePathInput {
  routeId: string;
  polyline: Point[];
}

/**
 * The horizontal / vertical pixel gap between adjacent lanes when two
 * routes share a corridor. 8 px reads as "two distinct lines" at
 * typical floor-plan zoom without looking spread out.
 */
export const DEFAULT_LANE_GAP_PX = 8;

/**
 * Two segments are considered to share an axis bin if their fixed
 * coordinates differ by ≤ this many px. gridAstar emits cell-centre
 * coords (cellSize/2 + n*cellSize); cellSize = 4 → segment endpoints
 * snap to half-cells. 6 px absorbs that without false-positiving.
 */
const OVERLAP_TOLERANCE_PX = 6;

type Axis = "H" | "V" | "D";

interface SegmentMeta {
  routeIdx: number;
  segIdx: number;     // index of the FIRST point of the segment within the route's polyline
  axis: Axis;
  fixedCoord: number; // y for H, x for V
  lo: number;         // min varying coord
  hi: number;         // max varying coord
  lane: number;       // assigned later (default 0)
}

/**
 * Apply lane offsets so parallel-overlapping segments from different
 * routes are visually separated. Returns a new array of RoutePathInput
 * with adjusted polylines — input is not mutated.
 */
export function applyLaneOffsets(
  routes: readonly RoutePathInput[],
  gapPx: number = DEFAULT_LANE_GAP_PX,
): RoutePathInput[] {
  if (routes.length < 2) {
    // No possibility of cross-route overlap. Return shallow copies so
    // callers can rely on immutability of inputs.
    return routes.map((r) => ({ routeId: r.routeId, polyline: r.polyline.map((p) => ({ ...p })) }));
  }

  // 1. Decompose every route into per-segment metadata.
  const segments: SegmentMeta[] = [];
  for (let r = 0; r < routes.length; r++) {
    const poly = routes[r].polyline;
    for (let i = 0; i < poly.length - 1; i++) {
      const a = poly[i];
      const b = poly[i + 1];
      let axis: Axis = "D";
      let fixedCoord = 0;
      let lo = 0;
      let hi = 0;
      if (a.y === b.y) {
        axis = "H";
        fixedCoord = a.y;
        lo = Math.min(a.x, b.x);
        hi = Math.max(a.x, b.x);
      } else if (a.x === b.x) {
        axis = "V";
        fixedCoord = a.x;
        lo = Math.min(a.y, b.y);
        hi = Math.max(a.y, b.y);
      }
      segments.push({ routeIdx: r, segIdx: i, axis, fixedCoord, lo, hi, lane: 0 });
    }
  }

  // 2. Bin axis-aligned segments by fixedCoord (rounded to tolerance)
  //    and find groups of overlapping segments within each bin. Groups
  //    of fewer than 2 routes (e.g. one segment, or two segments of the
  //    same route) get no lane assignment.
  assignLanes(segments.filter((s) => s.axis === "H"));
  assignLanes(segments.filter((s) => s.axis === "V"));

  // 3. Rebuild polylines with the assigned offsets applied.
  // Index segments by (routeIdx, segIdx) for fast lookup.
  const segIndex = new Map<string, SegmentMeta>();
  for (const s of segments) {
    segIndex.set(`${s.routeIdx}:${s.segIdx}`, s);
  }

  return routes.map((route, r) => {
    const poly = route.polyline;
    if (poly.length < 2) {
      return { routeId: route.routeId, polyline: poly.map((p) => ({ ...p })) };
    }
    const out: Point[] = [];
    // First point: original (so marker pin still owns the start position).
    out.push({ ...poly[0] });
    for (let i = 1; i < poly.length - 1; i++) {
      const prev = segIndex.get(`${r}:${i - 1}`);
      const curr = segIndex.get(`${r}:${i}`);
      let x = poly[i].x;
      let y = poly[i].y;
      // For an interior corner between two axis-aligned segments, the
      // corner's x is determined by whichever segment is V (fixed x);
      // the y is determined by whichever is H (fixed y). After offset,
      // V's x shifts by laneOffset, H's y shifts.
      const adjustFromV = (s: SegmentMeta | undefined): void => {
        if (s && s.axis === "V") x = poly[i].x + s.lane * gapPx;
      };
      const adjustFromH = (s: SegmentMeta | undefined): void => {
        if (s && s.axis === "H") y = poly[i].y + s.lane * gapPx;
      };
      adjustFromV(prev);
      adjustFromV(curr);
      adjustFromH(prev);
      adjustFromH(curr);
      // Two H in a row (degenerate after dedup) — take outgoing y offset.
      if (prev?.axis === "H" && curr?.axis === "H") {
        y = poly[i].y + curr.lane * gapPx;
      }
      // Two V in a row — take outgoing x offset.
      if (prev?.axis === "V" && curr?.axis === "V") {
        x = poly[i].x + curr.lane * gapPx;
      }
      out.push({ x, y });
    }
    // Last point: original.
    out.push({ ...poly[poly.length - 1] });
    return { routeId: route.routeId, polyline: out };
  });
}

/**
 * Within a list of segments sharing the same axis (all H or all V),
 * find overlap groups and stamp `.lane` on each segment.
 *
 * Bin first by fixedCoord (rounded to OVERLAP_TOLERANCE_PX), then
 * within each bin run a sweep-line union over [lo, hi]. A "group" is
 * a maximally connected set under the overlap relation. Only groups
 * containing segments from 2+ distinct routes get lane assignments.
 */
function assignLanes(segments: SegmentMeta[]): void {
  const bins = new Map<number, SegmentMeta[]>();
  for (const s of segments) {
    const key = Math.round(s.fixedCoord / OVERLAP_TOLERANCE_PX) * OVERLAP_TOLERANCE_PX;
    let list = bins.get(key);
    if (!list) { list = []; bins.set(key, list); }
    list.push(s);
  }
  for (const bin of bins.values()) {
    if (bin.length < 2) continue;
    bin.sort((a, b) => a.lo - b.lo);
    let group: SegmentMeta[] = [];
    let groupMax = -Infinity;
    for (const s of bin) {
      if (group.length === 0 || s.lo > groupMax) {
        finaliseGroup(group);
        group = [s];
        groupMax = s.hi;
      } else {
        group.push(s);
        groupMax = Math.max(groupMax, s.hi);
      }
    }
    finaliseGroup(group);
  }
}

function finaliseGroup(group: SegmentMeta[]): void {
  if (group.length < 2) return;
  // Distinct route indices in this group, stably sorted ascending.
  const uniqueRoutes = [...new Set(group.map((s) => s.routeIdx))].sort((a, b) => a - b);
  if (uniqueRoutes.length < 2) return;
  // Symmetric lanes centred on 0: for N routes, lane[i] = i - (N-1)/2.
  const N = uniqueRoutes.length;
  const laneFor = new Map<number, number>();
  for (let i = 0; i < N; i++) {
    laneFor.set(uniqueRoutes[i], i - (N - 1) / 2);
  }
  for (const s of group) {
    s.lane = laneFor.get(s.routeIdx) ?? 0;
  }
}
